import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { FiCheck, FiChevronLeft, FiChevronRight, FiClock, FiX } from "react-icons/fi";
import getUserDisplayName from "@/features/timeOff/utils/getUserDisplayName";
const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-800 border border-amber-200",
    approved: "bg-green-100 text-green-800 border border-green-200",
    rejected: "bg-rose-100 text-rose-800 border border-rose-200",
};
function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function addMonths(date, months) {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
}
// Start Monday, End Sunday
function startOfWeekMonday(date) {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7; // 0=Mon, 6=Sun
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
}
function endOfWeekSunday(date) {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
    const toAdd = 6 - day;
    d.setDate(d.getDate() + toAdd);
    d.setHours(23, 59, 59, 999);
    return d;
}
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function formatMonthYear(date) {
    return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date);
}
function dayLabel(date) {
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit" }).format(date);
}
const weekdayShort = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]; // Monday first
function hexToRgb(hex) {
    if (!hex)
        return null;
    let h = hex.trim();
    // support rgb() strings too: return null to let browser handle default text color
    if (!h.startsWith("#"))
        return null;
    if (h.length === 4) {
        // #RGB -> #RRGGBB
        h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    }
    if (h.length !== 7)
        return null;
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    if ([r, g, b].some((v) => Number.isNaN(v)))
        return null;
    return { r, g, b };
}
function getReadableTextColor(bgColor) {
    const rgb = hexToRgb(bgColor ?? undefined);
    if (!rgb)
        return undefined;
    // YIQ formula
    const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return yiq >= 128 ? "#111827" /* gray-900 */ : "#ffffff";
}
const STATUS_ICONS = {
    approved: _jsx(FiCheck, { className: "inline -mt-0.5" }),
    pending: _jsx(FiClock, { className: "inline -mt-0.5" }),
    rejected: _jsx(FiX, { className: "inline -mt-0.5" }),
};
const TimeOffCalendar = ({ requests, holidays = [], onSelectRequest, onSelectRange, getTypeColor, canSeeNames = false, initialMonth, onMonthChange, }) => {
    const today = useMemo(() => new Date(), []);
    const [currentMonth, setCurrentMonth] = useState(startOfMonth(initialMonth ?? today));
    const handleMonthChange = (months) => {
        setCurrentMonth((previousMonth) => {
            const nextMonth = addMonths(previousMonth, months);
            onMonthChange?.(nextMonth);
            return nextMonth;
        });
    };
    useEffect(() => {
        if (!initialMonth)
            return;
        const normalized = startOfMonth(initialMonth);
        setCurrentMonth(normalized);
        onMonthChange?.(normalized);
    }, [initialMonth, onMonthChange]);
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeekMonday(monthStart);
    const gridEnd = endOfWeekSunday(monthEnd);
    const days = useMemo(() => {
        const dates = [];
        const d = new Date(gridStart);
        while (d <= gridEnd) {
            dates.push(new Date(d));
            d.setDate(d.getDate() + 1);
        }
        return dates;
    }, [gridStart, gridEnd]);
    const holidaysByDate = useMemo(() => {
        const map = new Map();
        for (const holiday of holidays) {
            const date = new Date(holiday.date);
            map.set(date.toDateString(), holiday);
        }
        return map;
    }, [holidays]);
    const [expandedDay, setExpandedDay] = useState(null);
    const [selectionStart, setSelectionStart] = useState(null);
    const [selectionEnd, setSelectionEnd] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const getRangeBounds = (start, end) => {
        if (!start || !end)
            return [null, null];
        return start <= end ? [start, end] : [end, start];
    };
    const resetSelection = () => {
        setIsSelecting(false);
        setSelectionStart(null);
        setSelectionEnd(null);
    };
    const isDateInRange = (date, start, end) => {
        if (!start || !end)
            return false;
        const rangeStart = new Date(start);
        const rangeEnd = new Date(end);
        rangeStart.setHours(0, 0, 0, 0);
        rangeEnd.setHours(23, 59, 59, 999);
        return date.getTime() >= rangeStart.getTime() && date.getTime() <= rangeEnd.getTime();
    };
    const eventsByDay = useMemo(() => {
        const map = new Map();
        for (const day of days) {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);
            const events = requests.filter((r) => {
                if (!r.start_at || !r.end_at)
                    return false;
                const rs = new Date(r.start_at);
                const re = new Date(r.end_at);
                return rs <= dayEnd && re >= dayStart; // overlap
            });
            map.set(day.toDateString(), events);
        }
        return map;
    }, [days, requests]);
    const handleDayMouseDown = (day, event) => {
        if (event.target.closest("button"))
            return;
        setSelectionStart(day);
        setSelectionEnd(day);
        setIsSelecting(true);
        event.preventDefault();
    };
    const handleDayMouseEnter = (day) => {
        if (!isSelecting)
            return;
        setSelectionEnd(day);
    };
    const handleDayMouseUp = (day, event) => {
        if (!isSelecting || !selectionStart)
            return;
        const [rangeStart, rangeEnd] = getRangeBounds(selectionStart, selectionEnd ?? day);
        if (rangeStart && rangeEnd) {
            onSelectRange?.(rangeStart, rangeEnd);
        }
        resetSelection();
        event.preventDefault();
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "mb-3 flex items-center justify-between", children: [_jsxs("div", { className: "inline-flex items-center gap-2", children: [_jsx("button", { type: "button", className: "rounded border px-2 py-1 text-gray-700 hover:bg-gray-50", "aria-label": "Mois pr\u00E9c\u00E9dent", onClick: () => handleMonthChange(-1), children: _jsx(FiChevronLeft, {}) }), _jsx("button", { type: "button", className: "rounded border px-2 py-1 text-gray-700 hover:bg-gray-50", "aria-label": "Mois suivant", onClick: () => handleMonthChange(1), children: _jsx(FiChevronRight, {}) }), _jsx("span", { className: "ml-2 text-sm font-medium text-gray-900 capitalize", children: formatMonthYear(currentMonth) })] }), _jsxs("div", { className: "flex items-center gap-3 text-xs text-gray-700", children: [_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(FiCheck, {}), " Valid\u00E9"] }), _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(FiClock, {}), " En attente"] }), _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(FiX, {}), " Refus\u00E9"] })] })] }), _jsxs("div", { className: "grid grid-cols-7 gap-px rounded-lg border bg-gray-200", onMouseLeave: resetSelection, children: [weekdayShort.map((w) => (_jsx("div", { className: "bg-gray-50 p-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500", children: w }, w))), days.map((day) => {
                        const inMonth = day.getMonth() === currentMonth.getMonth();
                        const isToday = isSameDay(day, today);
                        const dayKey = day.toDateString();
                        const events = eventsByDay.get(dayKey) ?? [];
                        const holiday = holidaysByDate.get(dayKey);
                        const isPublicHoliday = Boolean(holiday);
                        const maxVisible = 3;
                        const hasOverflow = events.length > maxVisible;
                        const isExpanded = expandedDay === dayKey;
                        const visibleEvents = hasOverflow && !isExpanded ? events.slice(0, maxVisible) : events;
                        const extra = hasOverflow ? events.length - maxVisible : 0;
                        const [rangeStart, rangeEnd] = getRangeBounds(selectionStart, selectionEnd);
                        const isInSelection = !isPublicHoliday && isDateInRange(day, rangeStart, rangeEnd);
                        const ringClass = isToday ? "ring-1 ring-blue-500" : isInSelection ? "ring-1 ring-blue-200" : "";
                        const backgroundClass = isPublicHoliday
                            ? "bg-gray-100 text-gray-500"
                            : inMonth
                                ? "bg-white"
                                : "bg-gray-50 text-gray-400";
                        const selectionClass = isInSelection ? "bg-blue-50" : "";
                        return (_jsxs("div", { className: `min-h-[110px] p-2 ${backgroundClass} ${ringClass} ${selectionClass} ${isPublicHoliday ? "cursor-not-allowed" : ""}`, onMouseDown: (event) => {
                                if (isPublicHoliday)
                                    return;
                                handleDayMouseDown(day, event);
                            }, onMouseEnter: () => {
                                if (isPublicHoliday)
                                    return;
                                handleDayMouseEnter(day);
                            }, onMouseUp: (event) => {
                                if (isPublicHoliday) {
                                    resetSelection();
                                    return;
                                }
                                handleDayMouseUp(day, event);
                            }, children: [_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsx("span", { className: `text-xs ${inMonth ? "text-gray-700" : "text-gray-400"}`, children: dayLabel(day) }), holiday && (_jsx("span", { className: "ml-2 max-w-[75%] truncate rounded bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-700", title: holiday.label, children: holiday.label }))] }), _jsxs("div", { className: "space-y-1", children: [visibleEvents.map((ev) => {
                                            const label = canSeeNames ? getUserDisplayName(ev.user) : ev.type;
                                            const bg = getTypeColor ? getTypeColor(ev.type) : undefined;
                                            const textColor = getReadableTextColor(bg);
                                            const style = bg
                                                ? { backgroundColor: bg, borderColor: bg, color: textColor }
                                                : undefined;
                                            const classes = `w-full truncate rounded px-2 py-1 text-left text-[11px] ${bg ? "border" : STATUS_STYLES[ev.status ?? "pending"]}`;
                                            const icon = STATUS_ICONS[ev.status ?? "pending"];
                                            return (_jsx("button", { type: "button", className: classes, style: style, title: `${ev.type} • ${new Date(ev.start_at).toLocaleString("fr-FR")} → ${new Date(ev.end_at).toLocaleString("fr-FR")}`, onClick: () => onSelectRequest && onSelectRequest(ev), children: _jsxs("span", { className: "inline-flex items-center gap-1", children: [icon, _jsx("span", { className: "truncate", children: label })] }) }, ev.id ?? `${ev.type}-${ev.start_at}`));
                                        }), hasOverflow && (_jsx("button", { type: "button", className: "text-[11px] font-medium text-blue-600 hover:underline", onClick: () => setExpandedDay(isExpanded ? null : dayKey), children: isExpanded ? "Réduire" : `+${extra} autre(s)` }))] })] }, day.toISOString()));
                    })] })] }));
};
export default TimeOffCalendar;
